import { FunctionComponent } from "react";

import { faBolt } from "@fortawesome/pro-solid-svg-icons";
import { faPlus, faShareNodes, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { ListPerson } from "../../../../../schema/list";

interface ReviewLeadActionsProps {
	row: ListPerson;
	onAssignClick?: () => void;
	onIgnoreClick?: () => void;
	/** Placeholder rows ("Find leads") offer no assignment. */
	isFindLeadRow: boolean;
	isTrackerList: boolean;
	/** Selects the lead before the assign picker opens. */
	onBeforeAssign: () => void;
}

/** The label and icon of the assign button: confirm, assign, or create a campaign first. */
export const resolveAssignAction = (row: ListPerson) => {
	const validCampaignCount = Object.values(
		(row.list?.campaignConfig ?? {}) as Record<string, { isValid?: boolean }>
	).filter((campaign) => campaign?.isValid).length;
	const isPreAssigned = !!row.campaignId;

	if (isPreAssigned) {
		return { icon: faBolt, label: "Confirm & send", isCreate: false };
	}

	return validCampaignCount === 0
		? { icon: faPlus, label: "Create campaign", isCreate: true }
		: { icon: faShareNodes, label: "Assign to campaign", isCreate: false };
};

/**
 * The Review-to-send hover bar under a lead card: "Ignore lead" and the assign action. It hangs
 * below the card (`top-full`), so the card's wrapper must be `relative group/leadcard`. Extracted
 * from `Lead.tsx` so the compact Review-to-send card keeps both actions (PREC-4175).
 */
export const ReviewLeadActions: FunctionComponent<ReviewLeadActionsProps> = ({
	row,
	onAssignClick,
	onIgnoreClick,
	isFindLeadRow,
	isTrackerList,
	onBeforeAssign,
}) => {
	if (!onAssignClick && !onIgnoreClick) {
		return null;
	}

	const assign = resolveAssignAction(row);
	// On tracker lists the "Create campaign" action is secondary, so render it as a muted outline
	// button instead of the prominent filled success button.
	const isMutedCreate = isTrackerList && assign.isCreate;

	return (
		<div className="hidden group-hover/leadcard:flex absolute left-0 right-0 top-full z-50 flex-row gap-2 p-2 bg-white border border-border-light rounded-b-xl shadow-md">
			{onIgnoreClick && (
				<button
					className="flex-1 cursor-pointer flex items-center justify-center gap-2 py-2 px-3 rounded-lg border border-status-error/40 text-status-error font-semibold text-sm hover:bg-status-error-light"
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onIgnoreClick();
					}}
				>
					<Icon className="w-3.5 h-3.5" icon={faXmark} />
					Ignore lead
				</button>
			)}
			{!isFindLeadRow && onAssignClick && (
				<button
					className={classNames(
						"flex-1 cursor-pointer flex items-center justify-center gap-2 py-2 px-3 rounded-lg font-semibold text-sm",
						isMutedCreate
							? "bg-white border border-border-light text-text hover:bg-brand-white"
							: "bg-status-success text-white hover:bg-status-success/90"
					)}
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onBeforeAssign();
						onAssignClick();
					}}
				>
					<Icon className="w-3.5 h-3.5" icon={assign.icon} />
					{assign.label}
				</button>
			)}
		</div>
	);
};
